import axios from '@/services/axios';


const BASE_PATH =
  '/financeiro/cobrancas/configuracoes';


const PARAMETER_FIELDS =
  Object.freeze([
    'codigo_banco',
    'codigo_beneficiario',
    'codigo_agencia',
    'codigo_posto',
    'nosso_numero_prefixo',
    'convenio',
    'carteira',
    'modalidade'
  ]);


function isPlainObject(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value)
  );
}


function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== ''
      )
  );
}


function requireInteger(
  value,
  fieldName,
  {
    minimum = 0
  } = {}
) {
  const number =
    Number(value);

  if (
    !Number.isSafeInteger(number) ||
    number < minimum
  ) {
    throw new Error(
      `Resposta administrativa inválida: ${fieldName}.`
    );
  }

  return number;
}


function requireString(
  value,
  fieldName
) {
  if (
    typeof value !== 'string' ||
    !value.trim()
  ) {
    throw new Error(
      `Resposta administrativa inválida: ${fieldName}.`
    );
  }

  return value.trim();
}


function nullableText(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (typeof value !== 'string') {
    throw new Error(
      'Resposta administrativa possui texto inválido.'
    );
  }

  const normalized =
    value.trim();

  return normalized || null;
}


function projectConfiguration(
  value
) {
  if (!isPlainObject(value)) {
    throw new Error(
      'Resposta administrativa de configuração inválida.'
    );
  }

  for (
    const field
    of [
      'permite_boleto_pix',
      'ativo',
      'credencial_configurada',
      'certificado_configurado',
      'webhook_secret_configurado'
    ]
  ) {
    if (
      typeof value[field] !==
        'boolean'
    ) {
      throw new Error(
        `Resposta administrativa inválida: ${field}.`
      );
    }
  }

  const result = {
    credenciais_origem: ['EXTERNAL_ENV', 'APPLICATION_MANAGED_ENCRYPTED'].includes(value.credenciais_origem)
      ? value.credenciais_origem : null,
    id:
      requireInteger(
        value.id,
        'id',
        {
          minimum: 1
        }
      ),

    conta_financeira_id:
      requireInteger(
        value.conta_financeira_id,
        'conta_financeira_id',
        {
          minimum: 1
        }
      ),

    conta_financeira_apelido:
      nullableText(
        value.conta_financeira_apelido
      ),

    conta_financeira_instituicao:
      nullableText(
        value.conta_financeira_instituicao
      ),

    provedor:
      requireString(
        value.provedor,
        'provedor'
      ),

    adaptador:
      requireString(
        value.adaptador,
        'adaptador'
      ),

    ambiente:
      requireString(
        value.ambiente,
        'ambiente'
      ),

    permite_boleto_pix:
      value.permite_boleto_pix,

    ativo:
      value.ativo,

    credencial_configurada:
      value.credencial_configurada,

    certificado_configurado:
      value.certificado_configurado,

    webhook_secret_configurado:
      value.webhook_secret_configurado,

    beneficiario_conferencia: value.beneficiario_conferencia || null,
    selecao_automatica: value.selecao_automatica === true,
    quantidade_cobrancas:
      requireInteger(
        value.quantidade_cobrancas,
        'quantidade_cobrancas'
      ),

    atualizado_em:
      value.atualizado_em ??
      null
  };

  for (
    const field
    of PARAMETER_FIELDS
  ) {
    result[field] =
      nullableText(
        value[field]
      );
  }

  return result;
}


function normalizeListResponse(
  payload
) {
  if (
    !isPlainObject(payload) ||
    !Array.isArray(payload.data) ||
    !isPlainObject(payload.paginacao)
  ) {
    throw new Error(
      'Resposta administrativa de listagem inválida.'
    );
  }

  return {
    data:
      payload.data.map(
        projectConfiguration
      ),

    paginacao: {
      pagina:
        requireInteger(
          payload.paginacao.pagina,
          'paginacao.pagina',
          {
            minimum: 1
          }
        ),

      limite:
        requireInteger(
          payload.paginacao.limite,
          'paginacao.limite',
          {
            minimum: 1
          }
        ),

      total_itens:
        requireInteger(
          payload
            .paginacao
            .total_itens,
          'paginacao.total_itens'
        ),

      total_paginas:
        requireInteger(
          payload
            .paginacao
            .total_paginas,
          'paginacao.total_paginas'
        )
    }
  };
}


function normalizeDefinitions(
  value,
  fieldName
) {
  if (!Array.isArray(value)) {
    throw new Error(
      `Catálogo administrativo inválido: ${fieldName}.`
    );
  }

  return value.map(
    definition => {
      if (
        !isPlainObject(definition) ||
        typeof definition.obrigatorio !==
          'boolean'
      ) {
        throw new Error(
          `Catálogo administrativo inválido: ${fieldName}.`
        );
      }

      return {
        campo:
          requireString(
            definition.campo,
            `${fieldName}.campo`
          ),

        obrigatorio:
          definition.obrigatorio
      };
    }
  );
}


function normalizeCatalogResponse(
  payload
) {
  if (
    !isPlainObject(payload) ||
    !Array.isArray(payload.data)
  ) {
    throw new Error(
      'Resposta administrativa do catálogo inválida.'
    );
  }

  return payload.data.map(
    value => {
      if (
        !isPlainObject(value) ||
        !Array.isArray(
          value.ambientes
        )
      ) {
        throw new Error(
          'Entrada administrativa do catálogo inválida.'
        );
      }

      const ambientes =
        value.ambientes.map(
          environment =>
            requireString(
              environment,
              'ambiente'
            )
        );

      if (
        ambientes.length === 0
      ) {
        throw new Error(
          'Entrada administrativa do catálogo sem ambiente.'
        );
      }

      return {
        provedor:
          requireString(
            value.provedor,
            'provedor'
          ),

        adaptador:
          requireString(
            value.adaptador,
            'adaptador'
          ),

        ambientes,

        parametros:
          normalizeDefinitions(
            value.parametros,
            'parametros'
          ),

        referencias_segredo:
          normalizeDefinitions(
            value.referencias_segredo,
            'referencias_segredo'
          )
      };
    }
  );
}


function normalizeAccountsResponse(
  payload
) {
  const rows =
    Array.isArray(payload)
      ? payload
      : (
          isPlainObject(payload) &&
          Array.isArray(payload.data)
            ? payload.data
            : null
        );

  if (!rows) {
    throw new Error(
      'Resposta de contas financeiras inválida.'
    );
  }

  return rows.map(
    value => {
      if (!isPlainObject(value)) {
        throw new Error(
          'Conta financeira inválida.'
        );
      }

      return {
        boletos_habilitados: value.boletos_habilitados === true,
        id:
          requireInteger(
            value.id,
            'conta.id',
            {
              minimum: 1
            }
          ),

        apelido:
          nullableText(
            value.apelido
          ),

        instituicao:
          nullableText(
            value.instituicao
          ),

        agencia:
          nullableText(
            value.agencia
          ),

        numero_conta:
          nullableText(
            value.numero_conta
          )
      };
    }
  );
}


function requireId(id) {
  return requireInteger(
    id,
    'id',
    {
      minimum: 1
    }
  );
}


function normalizeStateMutationResponse(
  payload,
  {
    flag,
    expectedActive
  } = {}
) {
  if (
    !isPlainObject(payload) ||
    typeof flag !== 'string' ||
    !flag ||
    typeof expectedActive !==
      'boolean' ||
    typeof payload[flag] !==
      'boolean' ||
    typeof payload.idempotente !==
      'boolean'
  ) {
    throw new Error(
      'Resposta administrativa de alteração de estado inválida.'
    );
  }

  const configuracao =
    projectConfiguration(
      payload.configuracao
    );

  if (
    configuracao.ativo !==
      expectedActive
  ) {
    throw new Error(
      'Resposta administrativa possui estado divergente.'
    );
  }

  return {
    configuracao,
    [flag]:
      payload[flag],
    idempotente:
      payload.idempotente
  };
}


async function listar(
  params = {}
) {
  const response =
    await axios.get(
      BASE_PATH,
      {
        params:
          cleanParams(params)
      }
    );

  return normalizeListResponse(
    response.data
  );
}


async function obterCatalogo() {
  const response =
    await axios.get(
      `${BASE_PATH}/catalogo`
    );

  return normalizeCatalogResponse(
    response.data
  );
}


async function detalhar(id) {
  const normalizedId =
    requireId(id);

  const response =
    await axios.get(
      `${BASE_PATH}/${normalizedId}`
    );

  return projectConfiguration(
    response.data.configuracao
  );
}


async function criar(payload) {
  if (!isPlainObject(payload)) {
    throw new Error(
      'Payload de configuração inválido.'
    );
  }

  const response =
    await axios.post(
      BASE_PATH,
      payload
    );

  return projectConfiguration(
    response.data.configuracao
  );
}


async function atualizar(
  id,
  payload
) {
  const normalizedId =
    requireId(id);

  if (!isPlainObject(payload)) {
    throw new Error(
      'Payload de configuração inválido.'
    );
  }

  const response =
    await axios.patch(
      `${BASE_PATH}/${normalizedId}`,
      payload
    );

  return projectConfiguration(
    response.data.configuracao
  );
}


async function listarContasFinanceiras() {
  const response =
    await axios.get(
      '/financeiro/contas-financeiras',
      {
        params: {
          limit: 'all',
          sort: 'apelido',
          order: 'asc'
        }
      }
    );

  return normalizeAccountsResponse(
    response.data
  );
}


async function ativar(id) {
  const normalizedId =
    requireId(id);

  const response =
    await axios.post(
      `${BASE_PATH}/${normalizedId}/ativar`,
      {}
    );

  return normalizeStateMutationResponse(
    response.data,
    {
      flag: 'ativada',
      expectedActive: true
    }
  );
}


async function desativar(id) {
  const normalizedId =
    requireId(id);

  const response =
    await axios.post(
      `${BASE_PATH}/${normalizedId}/desativar`,
      {}
    );

  return normalizeStateMutationResponse(
    response.data,
    {
      flag: 'desativada',
      expectedActive: false
    }
  );
}


export const configuracoesCobrancasApi = {
  async obterBeneficiarioConta(id) { return (await axios.get(`${BASE_PATH}/contas/${id}/beneficiario`)).data; },
  listar,
  obterCatalogo,
  detalhar,
  criar,
  atualizar,
  listarContasFinanceiras,
  ativar,
  desativar
};


export default configuracoesCobrancasApi;
